import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SetInitialStateFacade } from '../../../../common/models';
import { ConfirmationDialogComponent } from '../../../../ui-kit/confirmation-dialog/confirmation-dialog.component';
import {
  CatalogSearchCriteriaFacade,
  CatalogSearchCriteriaStateModel,
  CatalogSearchCriteriaType,
} from '../../catalog-search-criteria';
import { AllocationViewProfile } from '../allocation-view-profiles/models/allocation-view-profile.model';
import {
  AllocationViewClearHighlightedUli,
  AllocationViewClearLastChangedUli,
  AllocationViewInitialState,
  AllocationViewSelectSavedViewMode,
  AllocationViewSetCalendarEdit,
  AllocationViewSetFixedTimelineValue,
  AllocationViewSetHighlightedSlot,
  AllocationViewSetHighlightedUli,
  AllocationViewSetLastChangedUliAsHighlighted,
  AllocationViewSetLastSelectedUli,
  AllocationViewSetProfile,
  AllocationViewSetViewMode,
  AllocationViewTriggerRefresh,
} from './allocation-view.actions';
import { AllocationViewState, AllocationViewStateModel } from './allocation-view.state';
import { AllocationViewFilterName } from './constants';
import { ALLOCATION_TERMINAL_VIEW_DEFAULT_PAGE_SIZE, AllocationViewMode } from './constants/allocation-view.constant';
import { AllocationViewFiltersModel, AllocationViewListSearchCriteria } from './models';

@Injectable({ providedIn: 'root' })
export class AllocationViewFacade implements SetInitialStateFacade {
  selectedViewMode$: Observable<AllocationViewMode> = this.store.select(AllocationViewState.getSelectedViewMode);
  highlightedUli$: Observable<string> = this.store.select(AllocationViewState.getHighlightedUli);
  highlightedSlot$: Observable<number> = this.store.select(AllocationViewState.getHighlightedSlot);
  lastChangedUli$: Observable<string> = this.store.select(AllocationViewState.getLastChangedUli);
  lastSelectedUli$: Observable<string> = this.store.select(AllocationViewState.getLastSelectedUli);
  selectedProfile$: Observable<AllocationViewProfile> = this.store.select(AllocationViewState.getSelectedProfile);
  isTimelineFixed$: Observable<boolean> = this.store.select(AllocationViewState.getFixedTimeline);
  refreshTrigger$: Observable<boolean> = this.store.select(AllocationViewState.getRefreshTrigger);
  isEditCalendar$: Observable<boolean> = this.store.select(AllocationViewState.getIsCalendarEdit);
  oldFilters: AllocationViewListSearchCriteria;

  filters$: Observable<AllocationViewListSearchCriteria> = this.catalogSearchCriteriaFacade.state$.pipe(
    map(
      (catalogSearchCriteria: CatalogSearchCriteriaStateModel) =>
        catalogSearchCriteria[CatalogSearchCriteriaType.AllocationView],
    ),
    filter(Boolean),
  );

  constructor(private store: Store, private catalogSearchCriteriaFacade: CatalogSearchCriteriaFacade) {}

  getStateSnapshot(): AllocationViewStateModel {
    return this.store.selectSnapshot(AllocationViewState.getState);
  }

  setViewMode(viewMode: AllocationViewMode): void {
    this.store.dispatch(new AllocationViewSetViewMode(viewMode));
  }

  selectSavedViewMode(): void {
    this.store.dispatch(new AllocationViewSelectSavedViewMode());
  }

  setLastSelectedUli(uli: string): void {
    this.store.dispatch(new AllocationViewSetLastSelectedUli(uli));
  }

  highlightLastChangedUli(): void {
    this.store.dispatch(new AllocationViewSetLastChangedUliAsHighlighted());
  }

  setHighlightedUli(uli: string): void {
    this.store.dispatch(new AllocationViewSetHighlightedUli(uli));
  }

  setProfile(profile: AllocationViewProfile): void {
    this.store.dispatch(new AllocationViewSetProfile(profile));
  }

  clearHighlightedUli(): void {
    this.store.dispatch(new AllocationViewClearHighlightedUli());
  }

  clearLastChangedUli(): void {
    this.store.dispatch(new AllocationViewClearLastChangedUli());
  }

  setHighlightedSlot(slotId: number, dialog?: MatDialogRef<ConfirmationDialogComponent>): void {
    this.store.dispatch(new AllocationViewSetHighlightedSlot({ slotId, dialog }));
  }

  setFixedTimeline(value?: boolean): void {
    this.store.dispatch(new AllocationViewSetFixedTimelineValue(value));
  }

  setCalendarEdit(isEditing: boolean): void {
    this.store.dispatch(new AllocationViewSetCalendarEdit(isEditing));
  }

  setFilters(updatedFilters: Partial<AllocationViewFiltersModel>): void {
    this.getFilters().subscribe((oldFilters: AllocationViewListSearchCriteria) => {
      this.oldFilters = oldFilters;
      const filters = new AllocationViewFiltersModel({ ...oldFilters, ...updatedFilters });
      const searchCriteria: AllocationViewListSearchCriteria = new AllocationViewListSearchCriteria({
        ...filters,
        page: 0,
        size: ALLOCATION_TERMINAL_VIEW_DEFAULT_PAGE_SIZE,
      });
      this.catalogSearchCriteriaFacade.changeSearchCriteriaByCatalogType(
        searchCriteria,
        CatalogSearchCriteriaType.AllocationView,
      );
    });
  }

  getFilters(): Observable<AllocationViewListSearchCriteria> {
    return this.filters$.pipe(take(1));
  }

  resetFilterGroup(groupName: AllocationViewFilterName): void {
    this.setFilters(
      groupName === AllocationViewFilterName.RegistrationId
        ? this.oldFilters
        : { [groupName]: new AllocationViewFiltersModel()[groupName] },
    );
  }

  resetFilters(): void {
    this.setFilters(new AllocationViewFiltersModel());
  }

  triggerRefresh(): void {
    this.store.dispatch(new AllocationViewTriggerRefresh(true));
  }

  setInitialState(): void {
    this.store.dispatch(new AllocationViewInitialState());
  }
}
